import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Shield, Users, Ticket, Loader2, Crown, Ban, Sparkles, Plus, ShieldCheck, UserCog, Building2, Save,
  TrendingUp, Repeat, BarChart3, ScrollText, Search, Trash2, Package as PackageIcon, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/auth/AuthContext";
import { isFounder } from "@/lib/access";
import {
  fetchAdminStats, fetchAdminUsers, grantUserPlan, grantUserRole, deactivateUser,
  fetchAdminCodes, createAdminCode, disableAdminCode,
} from "@/lib/admin";
import { deleteCode as apiDeleteCode } from "@/lib/adminPanel";
import { BusinessPanel, SubscriptionsPanel, AiUsagePanel, LogsPanel } from "@/components/admin/AdminPanels";
import PackageEditor from "@/components/admin/PackageEditor";
import ContentEditor from "@/components/admin/ContentEditor";
import { fetchCompanySettings, updateCompanySettings } from "@/lib/company";
import { useTranslation } from "@/i18n";
import { toast } from "sonner";

const DURATIONS = [1, 3, 6, 9, 12, "permanent"];
const USAGE = [1, 10, 100, "unlimited"];
const COMPANY_FIELDS = [
  "companyName", "businessId", "vat", "email", "supportEmail", "phone", "website",
  "privacyUrl", "termsUrl", "supportPage", "iban", "bic", "currency", "invoiceInfo",
  "supportHours", "faq",
];
// UI effect -> backend code type
const EFFECT_TO_TYPE = {
  assistant_access: "assistant",
  strategist_access: "strategist",
  admin_access: "admin",
  discount_placeholder: "discount",
};
const TYPE_TO_EFFECT = {
  assistant: "assistant_access",
  strategist: "strategist_access",
  admin: "admin_access",
  discount: "discount_placeholder",
  founder: "founder_access",
};

export default function AdminDashboard() {
  const { t, locale } = useTranslation();
  const { user, formatApiError } = useAuth();
  const founder = isFounder(user);

  const [tab, setTab] = useState("business");
  const [stats, setStats] = useState(null);
  const [scope, setScope] = useState("admin");
  const [users, setUsers] = useState([]);
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [userQuery, setUserQuery] = useState("");

  const effects = useMemo(
    () => ["assistant_access", "strategist_access", "admin_access", "discount_placeholder"],
    []
  );
  const [form, setForm] = useState({ effect: "assistant_access", duration: "permanent", usageLimit: 1, label: "", code: "", discountKind: "percentage", discountValue: "", discountDuration: "entire", discountCycles: 1, applicableCycle: "both", applicablePackage: "all" });
  const [creating, setCreating] = useState(false);

  const [company, setCompany] = useState(null);
  const [savingCompany, setSavingCompany] = useState(false);

  useEffect(() => {
    if (founder && tab === "company" && !company) {
      fetchCompanySettings().then(setCompany).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || e.message));
    }
  }, [founder, tab, company, formatApiError]);

  const onSaveCompany = async () => {
    setSavingCompany(true);
    try {
      const saved = await updateCompanySettings(company);
      setCompany(saved);
      toast.success(t("admin.company.saved"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingCompany(false);
    }
  };
  const setC = (k, v) => setCompany((c) => ({ ...c, [k]: v }));

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, u, c] = await Promise.all([fetchAdminStats(), fetchAdminUsers(), fetchAdminCodes()]);
      setStats(s.stats || s); setScope(s.scope || "admin"); setUsers(u); setCodes(c);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [formatApiError]);

  useEffect(() => { reload(); }, [reload]);

  const wrap = async (id, fn, okMsg) => {
    setBusyId(id);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const onGrantPlan = (u, plan) => wrap(u.id, () => grantUserPlan(u.id, plan), t("admin.planGranted", { plan: t(`settings.plans.${plan}`) }));
  const onGrantRole = (u, role) => wrap(u.id, () => grantUserRole(u.id, role), role === "admin" ? t("admin.adminGranted") : t("admin.adminRevoked"));
  const onDeactivate = (u) => wrap(u.id, () => deactivateUser(u.id), t("admin.userDeactivated"));
  const onDisableCode = (c) => wrap(c.id, () => disableAdminCode(c.id), t("admin.codeDisabled"));
  const onDeleteCode = (c) => wrap(c.id, () => apiDeleteCode(c.id), t("admin.panel.codes.deleted"));

  const onCreateCode = async () => {
    setCreating(true);
    try {
      const payload = {
        type: EFFECT_TO_TYPE[form.effect],
        duration: form.duration === "permanent" ? "permanent" : Number(form.duration),
        usageLimit: form.usageLimit === "unlimited" ? "unlimited" : Number(form.usageLimit),
        label: form.label || undefined,
        code: form.code ? form.code.trim() : undefined,
      };
      if (form.effect === "discount_placeholder") {
        payload.discountKind = form.discountKind;
        payload.discountValue = Number(form.discountValue);
        payload.discountDuration = form.discountDuration;
        payload.discountCycles = form.discountDuration === "cycles" ? Number(form.discountCycles) : undefined;
        payload.applicableCycle = form.applicableCycle;
        payload.applicablePackage = form.applicablePackage;
      }
      const c = await createAdminCode(payload);
      toast.success(t("admin.codeCreated", { code: c.code }));
      setForm((f) => ({ ...f, label: "", code: "" }));
      await reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString(locale) : "—");
  const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString(locale) : "—");
  const isProtected = (u) => u.protected || u.role === "founder";

  return (
    <div className="min-h-screen bg-background transition-colors duration-300 flex flex-col" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 backdrop-blur-xl bg-card/80 border-b border-border">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center gap-2">
          <Link to="/" className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="admin-back" aria-label={t("admin.backToHome")}><ArrowLeft className="h-4 w-4" /></Link>
          <div className="flex-1 flex items-center gap-2">
            <Shield className="h-4 w-4 text-slate-700" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Chronicles</p>
              <p className="font-display text-base font-semibold">{founder ? t("admin.titleFounder") : t("admin.titleAdmin")}</p>
            </div>
          </div>
          <LanguageSwitcher />
        </div>
      </header>

      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-6 space-y-6">
        {/* Founder-only internal area */}
        {founder ? (
          <section data-testid="admin-founder-area" className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <div className="flex items-center gap-2 mb-1.5"><Crown className="h-4 w-4 text-amber-700" /><h3 className="font-display text-base font-semibold text-amber-900">{t("admin.founderArea.title")}</h3></div>
            <ul className="text-sm text-amber-800 space-y-1">
              <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" />{t("admin.founderArea.active")}</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" />{t("admin.founderArea.protected")}</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5" />{t("admin.founderArea.unlimited")}</li>
            </ul>
            <p className="text-[12px] text-amber-700 mt-2 italic">{t("admin.founderArea.warning")}</p>
          </section>
        ) : null}

        {/* Overview / statistics */}
        <section data-testid="admin-stats" className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
          <StatCard label={t("admin.stats.total")} value={stats?.totalUsers} testid="stat-total" />
          <StatCard label={t("admin.stats.service")} value={stats?.serviceUsers} testid="stat-service" />
          <StatCard label={t("admin.stats.assistant")} value={stats?.assistantUsers} testid="stat-assistant" />
          <StatCard label={t("admin.stats.strategist")} value={stats?.strategistUsers} testid="stat-strategist" />
          <StatCard label={t("admin.stats.admins")} value={stats?.adminUsers} testid="stat-admins" />
          <StatCard label={t("admin.stats.activeCodes")} value={stats?.activeCodes} testid="stat-active-codes" />
          {scope === "founder" ? (
            <>
              <StatCard label={t("admin.stats.founders")} value={stats?.founderUsers} testid="stat-founders" />
              <StatCard label={t("admin.stats.disabledCodes")} value={stats?.disabledCodes} testid="stat-disabled-codes" />
              <StatCard label={t("admin.stats.expiredCodes")} value={stats?.expiredCodes} testid="stat-expired-codes" />
              <StatCard label={t("admin.stats.redemptions")} value={stats?.totalRedemptions} testid="stat-redemptions" />
              <StatCard label={t("admin.stats.inactiveEligible")} value={stats?.inactiveEligible} testid="stat-inactive" />
            </>
          ) : null}
        </section>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap" data-testid="admin-tabs">
          {[
            { id: "business", icon: TrendingUp, label: t("admin.panel.tabs.business") },
            { id: "users", icon: Users, label: t("admin.usersTab") },
            { id: "subscriptions", icon: Repeat, label: t("admin.panel.tabs.subscriptions") },
            { id: "codes", icon: Ticket, label: t("admin.codesTab") },
            { id: "aiusage", icon: BarChart3, label: t("admin.panel.tabs.aiUsage") },
            { id: "logs", icon: ScrollText, label: t("admin.panel.tabs.logs") },
            ...(founder ? [{ id: "packages", icon: PackageIcon, label: t("admin.packages.tab") }] : []),
            ...(founder ? [{ id: "content", icon: FileText, label: t("admin.content.tab") }] : []),
            ...(founder ? [{ id: "company", icon: Building2, label: t("admin.company.tab") }] : []),
          ].map(({ id, icon: Icon, label }) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={["inline-flex items-center gap-1.5 rounded-full px-4 h-9 text-sm border transition", tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-100"].join(" ")}>
              <Icon className="h-3.5 w-3.5" /> {label}
            </button>
          ))}
        </div>

        {tab === "business" ? <BusinessPanel /> : null}
        {tab === "subscriptions" ? <SubscriptionsPanel locale={locale} onChanged={reload} /> : null}
        {tab === "aiusage" ? <AiUsagePanel /> : null}
        {tab === "logs" ? <LogsPanel locale={locale} /> : null}
        {tab === "packages" && founder ? <PackageEditor /> : null}
        {tab === "content" && founder ? <ContentEditor /> : null}

        {tab === "users" || tab === "codes" || tab === "company" ? (
        loading ? (
          <div className="py-20 flex justify-center" data-testid="admin-loading"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : tab === "users" ? (
          <section data-testid="admin-users" className="space-y-3">
            <div className="relative max-w-sm">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input value={userQuery} onChange={(e) => setUserQuery(e.target.value)} placeholder={t("admin.panel.users.search")} className="pl-9 rounded-xl h-10" data-testid="admin-users-search" />
            </div>
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b border-border">
                  <th className="px-4 py-3 font-medium">{t("admin.col.name")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.col.alias")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.col.email")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.col.role")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.col.plan")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.col.status")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.col.lastLogin")}</th>
                  <th className="px-4 py-3 font-medium">{t("admin.col.created")}</th>
                  <th className="px-4 py-3 font-medium text-right">{t("admin.col.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {users.filter((u) => {
                  const s = userQuery.trim().toLowerCase();
                  if (!s) return true;
                  return [`${u.firstName} ${u.secondName}`, u.alias, u.recoveryEmail].some((v) => (v || "").toLowerCase().includes(s));
                }).map((u) => (
                  <tr key={u.id} className="border-b border-border/60 last:border-0" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-4 py-3 text-slate-800">{u.firstName} {u.secondName}</td>
                    <td className="px-4 py-3 text-slate-700">{u.alias}</td>
                    <td className="px-4 py-3 text-slate-500">{u.recoveryEmail}</td>
                    <td className="px-4 py-3">
                      {isProtected(u) ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-medium px-2 py-0.5" data-testid={`admin-protected-${u.id}`}>
                          <Crown className="h-3 w-3" /> {t("admin.protectedFounder")}
                        </span>
                      ) : (
                        <span className={["inline-flex rounded-full text-[11px] font-medium px-2 py-0.5", u.role === "admin" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"].join(" ")}>
                          {t(`settings.roles.${u.role}`)}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-700">{t(`settings.plans.${u.plan}`)}</td>
                    <td className="px-4 py-3">
                      <span className={["inline-flex rounded-full text-[11px] font-medium px-2 py-0.5", u.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"].join(" ")}>
                        {t(`admin.statuses.${u.status}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDateTime(u.lastLoginAt)}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(u.createdAt)}</td>
                    <td className="px-4 py-3">
                      {isProtected(u) ? (
                        <div className="flex items-center justify-end gap-1.5 text-amber-700 text-xs" data-testid={`admin-founder-locked-${u.id}`}>
                          <ShieldCheck className="h-3.5 w-3.5" /> {t("admin.protected")}
                        </div>
                      ) : (
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-slate-300" disabled={busyId === u.id} onClick={() => onGrantPlan(u, "assistant")} data-testid={`admin-grant-assistant-${u.id}`}>
                            <Sparkles className="h-3 w-3 mr-1" />{t("admin.grantAssistant")}
                          </Button>
                          <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-slate-300" disabled={busyId === u.id} onClick={() => onGrantPlan(u, "strategist")} data-testid={`admin-grant-strategist-${u.id}`}>
                            <Crown className="h-3 w-3 mr-1" />{t("admin.grantStrategist")}
                          </Button>
                          {u.role === "admin" ? (
                            <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-slate-300" disabled={busyId === u.id} onClick={() => onGrantRole(u, "user")} data-testid={`admin-revoke-admin-${u.id}`}>
                              <UserCog className="h-3 w-3 mr-1" />{t("admin.revokeAdmin")}
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-slate-300" disabled={busyId === u.id} onClick={() => onGrantRole(u, "admin")} data-testid={`admin-grant-admin-${u.id}`}>
                              <UserCog className="h-3 w-3 mr-1" />{t("admin.grantAdmin")}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-rose-200 text-rose-600 hover:bg-rose-50" disabled={busyId === u.id} onClick={() => onDeactivate(u)} data-testid={`admin-deactivate-${u.id}`}>
                            <Ban className="h-3 w-3 mr-1" />{t("admin.deactivate")}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </section>
        ) : tab === "codes" ? (
          <section data-testid="admin-codes" className="space-y-5">
            {/* Create code */}
            <div className="rounded-2xl border border-border bg-card p-4">
              <div className="flex items-center gap-2 mb-3"><Plus className="h-4 w-4 text-slate-700" /><h3 className="font-display text-base font-semibold">{t("admin.createCode")}</h3></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                <TextField label={t("admin.codeName")} value={form.label} onChange={(v) => setForm((f) => ({ ...f, label: v }))} placeholder={t("admin.codeNamePlaceholder")} testid="admin-code-name" />
                <TextField label={t("admin.codeValue")} value={form.code} onChange={(v) => setForm((f) => ({ ...f, code: v }))} placeholder={t("admin.codeValuePlaceholder")} testid="admin-code-value-input" mono />
                <SelectField label={t("admin.effectType")} value={form.effect} onChange={(v) => setForm((f) => ({ ...f, effect: v }))} testid="admin-code-effect"
                  options={effects.map((e) => ({ value: e, label: t(`admin.effects.${e}`) }))} />
                <SelectField label={t("admin.duration")} value={String(form.duration)} onChange={(v) => setForm((f) => ({ ...f, duration: v }))} testid="admin-code-duration"
                  options={DURATIONS.map((d) => ({ value: String(d), label: d === "permanent" ? t("admin.permanent") : t("admin.months", { count: d }) }))} />
                <SelectField label={t("admin.usageLimit")} value={String(form.usageLimit)} onChange={(v) => setForm((f) => ({ ...f, usageLimit: v }))} testid="admin-code-usage"
                  options={USAGE.map((u2) => ({ value: String(u2), label: u2 === "unlimited" ? t("admin.unlimited") : String(u2) }))} />
                {form.effect === "discount_placeholder" ? (
                  <>
                    <SelectField label={t("admin.discount.kind")} value={form.discountKind} onChange={(v) => setForm((f) => ({ ...f, discountKind: v }))} testid="admin-discount-kind"
                      options={[{ value: "percentage", label: t("admin.discount.percentage") }, { value: "fixed", label: t("admin.discount.fixed") }]} />
                    <TextField
                      label={form.discountKind === "percentage" ? t("admin.discount.amountPct") : t("admin.discount.amountFixed")}
                      value={String(form.discountValue)}
                      onChange={(v) => setForm((f) => ({ ...f, discountValue: v.replace(/[^0-9.]/g, "") }))}
                      placeholder={form.discountKind === "percentage" ? "1–100" : "> 0"}
                      testid="admin-discount-value" mono />
                    <SelectField label={t("admin.discount.duration")} value={form.discountDuration} onChange={(v) => setForm((f) => ({ ...f, discountDuration: v }))} testid="admin-discount-duration"
                      options={[{ value: "first_payment", label: t("admin.discount.firstPayment") }, { value: "cycles", label: t("admin.discount.cycles") }, { value: "entire", label: t("admin.discount.entire") }]} />
                    {form.discountDuration === "cycles" ? (
                      <TextField label={t("admin.discount.cyclesCount")} value={String(form.discountCycles)} onChange={(v) => setForm((f) => ({ ...f, discountCycles: v.replace(/[^0-9]/g, "") }))} testid="admin-discount-cycles" mono />
                    ) : null}
                    <SelectField label={t("admin.discount.applicableCycle")} value={form.applicableCycle} onChange={(v) => setForm((f) => ({ ...f, applicableCycle: v }))} testid="admin-discount-cycle"
                      options={[{ value: "monthly", label: t("admin.discount.monthly") }, { value: "yearly", label: t("admin.discount.yearly") }, { value: "both", label: t("admin.discount.both") }]} />
                    <SelectField label={t("admin.discount.applicablePackage")} value={form.applicablePackage} onChange={(v) => setForm((f) => ({ ...f, applicablePackage: v }))} testid="admin-discount-package"
                      options={[{ value: "service", label: "Service" }, { value: "assistant", label: "Assistant" }, { value: "strategist", label: "Strategist" }, { value: "all", label: t("admin.discount.allPackages") }]} />
                  </>
                ) : null}
                <div className="flex items-end">
                  <Button className="rounded-full h-11 w-full bg-slate-900 hover:bg-slate-800" onClick={onCreateCode} disabled={creating} data-testid="admin-create-code">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}{t("admin.createCode")}
                  </Button>
                </div>
              </div>
            </div>

            {/* Codes table */}
            <div className="rounded-2xl border border-border bg-card overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b border-border">
                    <th className="px-4 py-3 font-medium">{t("admin.col.code")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.col.effect")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.col.duration")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.col.usage")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.col.used")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.col.status")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.col.created")}</th>
                    <th className="px-4 py-3 font-medium">{t("admin.col.expires")}</th>
                    <th className="px-4 py-3 font-medium text-right">{t("admin.col.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {codes.map((c) => (
                    <tr key={c.id} className="border-b border-border/60 last:border-0" data-testid={`admin-code-row-${c.id}`}>
                      <td className="px-4 py-3 font-mono-tab text-slate-800" data-testid={`admin-code-value-${c.id}`}>
                        {c.code}
                        {c.label ? <span className="block text-[11px] text-slate-400 font-sans">{c.label}</span> : null}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{t(`admin.effects.${TYPE_TO_EFFECT[c.type] || c.type}`)}</td>
                      <td className="px-4 py-3 text-slate-600">{c.duration === "permanent" ? t("admin.permanent") : t("admin.months", { count: c.duration })}</td>
                      <td className="px-4 py-3 text-slate-600">{c.usageLimit === "unlimited" ? t("admin.unlimited") : c.usageLimit}</td>
                      <td className="px-4 py-3 text-slate-600 font-mono-tab" data-testid={`admin-code-used-${c.id}`}>{c.usedCount || 0}</td>
                      <td className="px-4 py-3">
                        <span className={["inline-flex rounded-full text-[11px] font-medium px-2 py-0.5", c.status === "active" ? "bg-emerald-100 text-emerald-700" : c.status === "expired" ? "bg-slate-200 text-slate-600" : "bg-rose-100 text-rose-700"].join(" ")} data-testid={`admin-code-status-${c.id}`}>
                          {t(`admin.statuses.${c.status}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{fmtDate(c.createdAt)}</td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{c.expiresAt ? fmtDate(c.expiresAt) : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {c.status === "active" ? (
                            <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-rose-200 text-rose-600 hover:bg-rose-50" disabled={busyId === c.id} onClick={() => onDisableCode(c)} data-testid={`admin-disable-code-${c.id}`}>
                              <Ban className="h-3 w-3 mr-1" />{t("admin.disable")}
                            </Button>
                          ) : null}
                          {(c.usedCount || 0) === 0 ? (
                            <Button size="sm" variant="outline" className="h-8 rounded-full text-xs border-slate-300 text-slate-600 hover:bg-slate-100" disabled={busyId === c.id} onClick={() => onDeleteCode(c)} data-testid={`admin-delete-code-${c.id}`}>
                              <Trash2 className="h-3 w-3 mr-1" />{t("admin.panel.codes.delete")}
                            </Button>
                          ) : null}
                          {c.status !== "active" && (c.usedCount || 0) > 0 ? <span className="text-xs text-slate-400">—</span> : null}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : (
          <section data-testid="admin-company" className="rounded-2xl border border-border bg-card p-5 space-y-5">
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-slate-700" />
              <h3 className="font-display text-base font-semibold">{t("admin.company.title")}</h3>
            </div>
            {!company ? (
              <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {COMPANY_FIELDS.map((k) => (
                    <TextField key={k} label={t(`admin.company.fields.${k}`)} value={company[k] ?? ""} onChange={(v) => setC(k, v)} testid={`admin-company-${k}`} />
                  ))}
                </div>
                <Button className="rounded-full h-11 bg-slate-900 hover:bg-slate-800" onClick={onSaveCompany} disabled={savingCompany} data-testid="admin-company-save">
                  {savingCompany ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />}{t("admin.company.save")}
                </Button>
              </>
            )}
          </section>
        )
        ) : null}
      </main>
    </div>
  );
}

function StatCard({ label, value, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-3" data-testid={testid}>
      <p className="font-display text-2xl font-semibold text-slate-900">{value ?? "—"}</p>
      <p className="text-[11px] text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid, mono }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={["rounded-xl h-11", mono ? "font-mono-tab" : ""].join(" ")} data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-medium">{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 rounded-xl border border-input bg-background px-3 text-sm"
        data-testid={testid}
      >
        {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
      </select>
    </div>
  );
}
